import { Cloud, CloudRain, Layers, Waves, Zap } from "lucide-react";

export const cloudTypes = [
  {
    name: "Cumulonimbus",
    slug: "cumulonimbus",
    altitude: "Tall storm cloud",
    icon: Zap,
    short: "A huge tower cloud that can bring heavy rain, lightning, strong gusts, and sudden rough conditions.",
    traits: "Cumulonimbus clouds grow very tall, often with a dark base and a spreading anvil-shaped top. They are the classic thunderstorm clouds.",
    simpleMeaning: "This cloud means the weather can change fast. Expect strong wind gusts, heavy rain, lightning, and poor visibility.",
    marineMeaning: "Avoid open water if possible. Reef early, watch radar, and prepare for squalls, lightning, and steep short waves.",
    lookFor: ["Very tall cloud towers", "Dark base", "Anvil-shaped top", "Heavy showers or lightning nearby"],
  },
  {
    name: "Cumulus",
    slug: "cumulus",
    altitude: "Low fair-weather cloud",
    icon: Waves,
    short: "Puffy white clouds with flat bottoms, often seen during pleasant weather.",
    traits: "Cumulus clouds look like cotton balls with clear edges and flat bases. They usually form in rising warm air during the day.",
    simpleMeaning: "Usually a good-weather cloud, but if it grows tall and dark it can develop into showers or storms.",
    marineMeaning: "Generally fine for boating, but keep watching if the clouds build vertically or wind starts increasing.",
    lookFor: ["Puffy cotton shape", "Flat base", "Blue sky between clouds", "Bright white tops"],
  },
  {
    name: "Nimbostratus",
    slug: "nimbostratus",
    altitude: "Low to mid-level rain cloud",
    icon: CloudRain,
    short: "A thick grey blanket cloud that brings steady rain or drizzle.",
    traits: "Nimbostratus covers most of the sky with a dark, flat, layered look. It often hides the sun completely.",
    simpleMeaning: "This cloud usually means long-lasting rain and dull visibility rather than short sharp showers.",
    marineMeaning: "Expect wet decks, reduced visibility, and uncomfortable conditions over a longer period.",
    lookFor: ["Dark grey sky", "Steady rain", "No clear cloud edges", "Low, gloomy ceiling"],
  },
  {
    name: "Stratocumulus",
    slug: "stratocumulus",
    altitude: "Low layered cloud",
    icon: Layers,
    short: "Low grey or white cloud patches arranged in rolls or clumps.",
    traits: "Stratocumulus clouds form broad, lumpy layers with gaps of brighter sky between them.",
    simpleMeaning: "Often means stable, slightly cloudy weather. Light drizzle is possible, but heavy rain is uncommon.",
    marineMeaning: "Usually manageable for boating, though it may feel cool, grey, and breezy.",
    lookFor: ["Low lumpy patches", "Rows or rolls", "Grey and white areas", "Some brighter gaps"],
  },
  {
    name: "Stratus",
    slug: "stratus",
    altitude: "Low sheet cloud",
    icon: Cloud,
    short: "A low flat grey layer, sometimes like fog lifted above the ground or sea.",
    traits: "Stratus clouds make the sky look flat and dull. They can sit very low and may produce mist or light drizzle.",
    simpleMeaning: "This cloud usually means calm but grey weather, with possible mist and lower visibility.",
    marineMeaning: "Watch visibility carefully near shore, traffic, buoys, and harbor entrances.",
    lookFor: ["Flat grey layer", "Low cloud base", "Mist or drizzle", "Little cloud texture"],
  },
  {
    name: "Altostratus",
    slug: "altostratus",
    altitude: "Mid-level sheet cloud",
    icon: Cloud,
    short: "A grey-blue sheet that dims the sun and often arrives before widespread rain.",
    traits: "Altostratus covers much of the sky. The sun may look like a pale disk behind the cloud.",
    simpleMeaning: "This often means a weather system is approaching and rain may arrive later.",
    marineMeaning: "Good time to review the forecast, plan shelter, and check if wind will rise with the front.",
    lookFor: ["Grey-blue sky", "Weak sun outline", "Wide smooth sheet", "Cloud base slowly lowering"],
  },
  {
    name: "Altocumulus",
    slug: "altocumulus",
    altitude: "Mid-level patch cloud",
    icon: Layers,
    short: "Rounded mid-level patches, often arranged in waves or groups.",
    traits: "Altocumulus clouds look like small rounded clumps, rolls, or ripples. They are larger than high cirrocumulus patches.",
    simpleMeaning: "Usually harmless, but in warm humid weather they can show instability and possible later storms.",
    marineMeaning: "Watch for building wind, humidity, and darker growing clouds later in the day.",
    lookFor: ["Rounded patches", "Wave-like rows", "White or grey color", "Mid-sky height"],
  },
  {
    name: "Cirrostratus",
    slug: "cirrostratus",
    altitude: "High veil cloud",
    icon: Cloud,
    short: "A thin high veil that can create halos around the sun or moon.",
    traits: "Cirrostratus is made of ice crystals and spreads like a transparent sheet across the sky.",
    simpleMeaning: "Often an early sign that a front is approaching, with thicker clouds and rain possible later.",
    marineMeaning: "Use it as an early warning to check the next 24–48 hours of weather before longer passages.",
    lookFor: ["Thin milky veil", "Halo around sun or moon", "Sky still bright", "Cloud thickening over time"],
  },
  {
    name: "Cirrocumulus",
    slug: "cirrocumulus",
    altitude: "High ripple cloud",
    icon: Layers,
    short: "Tiny white ripples high in the sky, sometimes called a mackerel sky.",
    traits: "Cirrocumulus appears as many small white grains or ripples, usually without shading.",
    simpleMeaning: "Usually fair weather, but it can show moisture high in the atmosphere.",
    marineMeaning: "Not normally dangerous by itself, but monitor if it appears with other signs of an approaching front.",
    lookFor: ["Tiny white ripples", "Mackerel-sky pattern", "Very high clouds", "Little or no grey shading"],
  },
  {
    name: "Cirrus",
    slug: "cirrus",
    altitude: "High wispy cloud",
    icon: Cloud,
    short: "Thin feather-like streaks high in the sky, often seen before weather changes.",
    traits: "Cirrus clouds are made of ice crystals and look like fine white hair, hooks, or feathers.",
    simpleMeaning: "Usually fair now, but can be an early sign that a front or weather change is coming.",
    marineMeaning: "Good early cue to check tomorrow’s forecast, especially before an offshore trip.",
    lookFor: ["Thin wispy streaks", "Feathery shape", "Very high altitude", "White and delicate"],
  },
];

export const getCloudBySlug = (slug) => cloudTypes.find((cloud) => cloud.slug === slug);

export function inferCloudName({ cloudCover = 0, rain = 0, windKnots = 0, gustKnots = 0 } = {}) {
  const gustSpread = Math.max(0, gustKnots - windKnots);

  if (rain > 3 || (rain > 0 && gustSpread >= 8)) return getCloudBySlug("cumulonimbus");
  if (rain > 0) return getCloudBySlug("nimbostratus");
  if (cloudCover >= 90) return getCloudBySlug("stratus");
  if (cloudCover >= 75) return getCloudBySlug("altostratus");
  if (cloudCover >= 55) return getCloudBySlug("stratocumulus");
  if (cloudCover >= 35) return getCloudBySlug("altocumulus");
  if (cloudCover >= 20) return getCloudBySlug("cirrostratus");
  if (cloudCover >= 10) return getCloudBySlug("cirrus");
  return getCloudBySlug("cirrus");
}